/**
 * HTTP client.
 *
 * Two responsibilities beyond issuing requests:
 *   • holds the access token in memory only — never localStorage, so an XSS payload
 *     cannot read it; the long-lived refresh token stays in an httpOnly cookie
 *   • transparently refreshes on a 401 and replays the original request, queueing any
 *     concurrent 401s behind a single refresh so a page with six parallel queries
 *     performs one token rotation rather than six
 */

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError, ApiResponse, PaginatedResponse } from '@ias/shared';

const BASE_URL = '/api/v1';

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered by the auth provider so a failed refresh can drop the session. */
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  // Sends the refresh cookie on /auth calls.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`);
  return config;
});

/** A normalised error the UI can branch on without knowing about axios. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly errors: ApiError[],
    readonly isNetworkError = false,
  ) {
    super(errors[0]?.message ?? 'Request failed');
    this.name = 'ApiRequestError';
  }

  /** Field → message, for handing straight to react-hook-form's setError. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries(
      this.errors.filter((e) => e.field).map((e) => [e.field!, e.message]),
    );
  }

  get code(): string {
    return this.errors[0]?.code ?? 'unknown';
  }
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  // Collapses concurrent refreshes into one in-flight request.
  refreshPromise ??= (async () => {
    try {
      const response = await axios.post<ApiResponse<{ accessToken: string }>>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const token = response.data.data.accessToken;
      setAccessToken(token);
      return token;
    } catch {
      setAccessToken(null);
      onUnauthenticated?.();
      return null;
    } finally {
      // Cleared on the next tick so queued callers all observe the same result.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();

  return refreshPromise;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ errors?: ApiError[] }>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (!error.response) {
      throw new ApiRequestError(
        0,
        [{ code: 'network_error', message: 'Δεν υπάρχει σύνδεση με τον διακομιστή / Cannot reach the server' }],
        true,
      );
    }

    const { status, data } = error.response;
    const isAuthCall = original?.url?.includes('/auth/');

    if (status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers.set('Authorization', `Bearer ${token}`);
        return http.request(original);
      }
    }

    if (status === 401 && !isAuthCall) onUnauthenticated?.();

    throw new ApiRequestError(
      status,
      data?.errors ?? [{ code: 'unknown_error', message: error.message }],
    );
  },
);

/* ── typed helpers ──────────────────────────────────────────────────────────── */

export async function getOne<T>(url: string, params?: unknown): Promise<T> {
  const response = await http.get<ApiResponse<T>>(url, { params });
  return response.data.data;
}

export async function getList<T>(url: string, params?: unknown): Promise<PaginatedResponse<T>> {
  const response = await http.get<PaginatedResponse<T>>(url, { params });
  return response.data;
}

/** List endpoint that returns a bare array (no pagination envelope). */
export async function getAll<T>(url: string, params?: unknown): Promise<T[]> {
  const response = await http.get<ApiResponse<T[]>>(url, { params });
  return response.data.data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.post<ApiResponse<T>>(url, body);
  return response.data.data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.put<ApiResponse<T>>(url, body);
  return response.data.data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.patch<ApiResponse<T>>(url, body);
  return response.data.data;
}

export async function remove(url: string): Promise<void> {
  await http.delete(url);
}

export async function upload<T>(url: string, file: File, fields: Record<string, string> = {}): Promise<T> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null && value !== '') form.append(key, value);
  }
  form.append('file', file);

  const response = await http.post<ApiResponse<T>>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
}

/** Triggers a browser download for an authenticated endpoint. */
export async function download(url: string, filename: string, params?: unknown): Promise<void> {
  const response = await http.get(url, { params, responseType: 'blob' });

  const disposition = String(response.headers['content-disposition'] ?? '');
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const resolved = utf8Match?.[1] ? decodeURIComponent(utf8Match[1]) : filename;

  const href = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = resolved;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

/**
 * Fetches a file for *display* rather than saving, and hands back an object URL.
 *
 * The detour through a blob is not incidental — it is the only thing that works here. The
 * access token lives in memory and is attached by the request interceptor above, so a
 * `<iframe src="/api/v1/files/…">` sends no Authorization header at all and the route
 * answers 401. Pulling the bytes through this client and pointing the viewer at a blob URL
 * keeps the file behind the same permission check as the download.
 *
 * `inline=true` flips the server's Content-Disposition from `attachment` to `inline`
 * (see the file route in the server's app.ts); without it a browser is entitled to save
 * the file instead of rendering it, which is the exact behaviour the preview exists to
 * avoid.
 *
 * The caller owns the returned URL and MUST revoke it — an unrevoked object URL pins its
 * blob in memory for the lifetime of the document, and these are whole PDFs.
 */
export async function openInline(url: string): Promise<{ url: string; mimeType: string }> {
  const response = await http.get(url, { params: { inline: 'true' }, responseType: 'blob' });
  const blob = response.data as Blob;

  return {
    url: URL.createObjectURL(blob),
    // Blob.type is populated from the response Content-Type; the header is the fallback
    // for the rare case a proxy strips it off the blob.
    mimeType: blob.type || String(response.headers['content-type'] ?? ''),
  };
}

export { BASE_URL };
